import { supplyChipHtml, supplyName } from "../supplies/supplyArt";
import { LevelCatalog } from "@highmarch/app";
import { ChestResult, Order, OrderReward, SECONDS_PER_DAY, grantReward, isComplete, utcDay } from "@highmarch/domain";
import { barHtml, btnHtml, fmtCompact } from "../kit";
import { icon } from "../iconsKit";
import { rewardFrom } from "../rewardFly/link";
import { chestIconUrl, resourceIconUrl } from "../portraits";
import { toast } from "../toast";
import { rowHtml } from "../components";
import { essenceIconHtml } from "../forgeArt";
import { fmtBadgeEta } from "./badges";
import { rewardChest } from "../chestReveal";
import { decorateEventsPage } from "./eventsPage";
import { dotHtml, showBandFocus } from "./evKit";
import { gemGlyph } from "./eventRewards";

/** What the Orders section needs from the hub: progress, the clock, and a save-and-redraw after every claim. */
export interface OrdersCtx {
  readonly catalog: LevelCatalog;
  readonly now: () => number;
  /** Saves and redraws the hub (pills, tab badge). */
  readonly onChange: () => void;
  /** Where toasts go. */
  readonly parent: HTMLElement;
  /** Opens the check-in card (the section's row when today's check-in is still waiting). */
  readonly onCheckin: () => void;
}

const MINUTES_PER_HOUR = 60;
const CHEST_LABELS = ["Common", "Uncommon", "Rare", "Epic", "Legendary", "Mythic"] as const;
/** Chest art on the two meters (hq/icons chest renders): the daily Supply chest and the bigger weekly one. */
const SUPPLY_ICON = "rare", WEEKLY_ICON = "epic";

/** "30m", "1h", "1h 30m": a speed-up's length. */
export function fmtSpeedUp(minutes: number): string {
  const h = Math.floor(minutes / MINUTES_PER_HOUR), m = minutes % MINUTES_PER_HOUR;
  return h === 0 ? `${m}m` : m === 0 ? `${h}h` : `${h}h ${m}m`;
}

/** An order's display line from its template copy: `{n}` → the target, `{tower}` → the picked tower's name. */
export function orderTitle(catalog: LevelCatalog, o: Order): string {
  const t = catalog.orders.template(o);
  const tower = o.towerId ? catalog.balance.towers[catalog.balance.towerIndex(o.towerId)].name : "";
  return (t?.title ?? o.id).replace("{n}", String(o.target)).replace("{tower}", tower);
}

/**
 * Lines for the "while you were away" card (another hub card calls this): new orders waiting, and anything ready to
 * claim. Empty when Orders are still locked or there is nothing new. Rolls the day over first.
 */
export function ordersAwayLines(catalog: LevelCatalog, now: number): string[] {
  const o = catalog.orders;
  if (!o.unlocked(catalog.highestLevelCleared)) return [];
  o.refresh(now, catalog.highestLevelCleared);
  const out: string[] = [];
  const fresh = o.newOrders(now).length;
  if (fresh > 0) out.push(`${fresh} new order${fresh === 1 ? "" : "s"} ready`);
  const ready = o.claimable();
  if (ready > 0) out.push(`${ready} reward${ready === 1 ? "" : "s"} to claim in Events`);
  return out;
}

/** Resource and speed-up chips for a reward. */
export function rewardHtml(gold: number, speedUpMinutes: number, unit = true): string {
  const res = gold > 0 ? `<span class="rw"><img decoding="sync" src="${resourceIconUrl("gold")}" alt="gold">${fmtCompact(gold)}</span>` : "";
  return `${res}${speedUpMinutes > 0 ? `<span class="rw su"><img decoding="sync" src="${resourceIconUrl("speedup")}" alt="">${fmtSpeedUp(speedUpMinutes)}${unit ? ` <span class="u">speed-up</span>` : ""}</span>` : ""}`;
}

/**
 * A reward claimed from `from` (an order, a check-in day, Tribute, an achievement, a pass tier, an event node): the
 * reward flight takes everything it paid home (gems and chests too, not only `gold`, which stays for the callers).
 */
export function flyReward(from: Element | null, _gold: number): void {
  rewardFrom(from);   // reward flight hook (ui/rewardFly/link.ts)
}

/** A chest's contents as one toast line. */
export function chestLine(name: string, chest: ChestResult, gearCount: number, essence = 0): string {
  const parts = chest.gold > 0 ? [`+${chest.gold} gold`] : [];
  if (gearCount > 0) parts.push(`${gearCount} gear`);
  if (essence > 0) parts.push(`+${essence} essence`);
  if (chest.speedUpMinutes > 0) parts.push(`${fmtSpeedUp(chest.speedUpMinutes)} speed-up`);
  return `${name} (${CHEST_LABELS[chest.rarity]}): ${parts.join(", ")}`;
}

/**
 * The Daily Orders section at the top of the Chests page: today's (and carried) orders with progress bars, CLAIM and
 * the free reroll, the Supply and weekly chest meters with OPEN, and today's check-in if it is still waiting.
 * Returns the section; it redraws itself after every action.
 */
export function mountOrders(sheet: HTMLElement, ctx: OrdersCtx): HTMLElement | null {
  const { catalog } = ctx;
  const orders = catalog.orders;
  decorateEventsPage(sheet);
  if (!orders.unlocked(catalog.highestLevelCleared)) return null;
  const sec = document.createElement("section");
  sec.className = "orders";
  (sheet.querySelector(".evpagebnr") ?? sheet.querySelector(".sheethead") ?? sheet.firstElementChild)?.after(sec);
  const render = (): void => {
    const now = ctx.now(), high = catalog.highestLevelCleared, d = catalog.balance.orders;
    orders.refresh(now, high);
    orders.markSeen(now);
    const today = utcDay(now);
    const shown = orders.orders.filter((o) => !o.claimed || o.day === today);
    const rerolls = orders.rerollsLeft(now);
    const rows = shown.map((o) => {
      const done = isComplete(o);
      const action = o.claimed ? `<span class="got">${icon("star", 16)} Claimed</span>`
        : done ? btnHtml("CLAIM", "gold", { className: "claim", data: `data-uid="${o.uid}"` })
        : rerolls > 0 ? `<button class="icbtn reroll" data-uid="${o.uid}" aria-label="Swap this order (free once a day)">${icon("rotate", 18)}</button>` : "";
      const kind = catalog.orders.template(o)?.kind === "hub" ? "hub" : "battle";
      const carried = o.day < today && !o.claimed ? `<small class="carry">from ${today - o.day === 1 ? "yesterday" : `${today - o.day} days ago`}</small>` : "";
      return `<div class="orow k-${kind}${done ? " done" : ""}${o.claimed ? " claimed" : ""}">
        <div class="oic">${icon(kind === "hub" ? "builder" : "sword", 16)}</div>
        <div class="otext"><b>${orderTitle(catalog, o)}</b>${carried}
          <div class="oprog">${barHtml(o.progress / o.target, done ? "gold" : "")}<small>${o.progress}/${o.target}</small></div></div>
        <div class="orew">${rewardHtml(o.reward.gold, o.reward.speedUpMinutes, false)}${o.reward.supply ? supplyChipHtml(catalog.balance, o.reward.supply, 1, 14) : ""}</div>
        <div class="oact">${action}</div>${done && !o.claimed ? dotHtml(1, true) : ""}</div>`;
    }).join("");
    const meter = (cls: string, rarity: string, name: string, have: number, goal: number, ready: number, extra = "") => `
      <div class="ochest ${cls}${ready > 0 ? " ready" : ""}"><button type="button" class="ochart" data-meter="${cls}" aria-label="${name}"><img decoding="sync" src="${chestIconUrl(rarity)}" alt=""></button>
        <div class="otext"><b>${name}</b>${extra ? `<span class="oxtra">${extra}</span>` : ""}${ready > 0 ? btnHtml("OPEN", "gold", { className: `open ${cls}` }) : `<div class="oprog">${barHtml(have / goal, "")}<small>${have}/${goal}</small></div>`}</div>
        ${dotHtml(ready)}</div>`;
    const weeklyExtra = `${d.weeklyEssence > 0 ? `<span class="rw">${essenceIconHtml(14)}${fmtCompact(d.weeklyEssence)}</span>` : ""}${d.weeklyGems > 0 ? `<span class="rw">${gemGlyph(14)}${d.weeklyGems}</span>` : ""}`;
    const checkin = catalog.checkin.canClaim(now)
      ? rowHtml({ tap: true, className: "ocheck", lead: `<span class="cki">${icon("star", 20)}</span>`, title: "Daily gift",
        sub: `Day ${catalog.checkin.slot + 1} of ${catalog.checkin.slots.length}`, end: `<span class="btn gold go"><span class="disp">CLAIM</span></span>${dotHtml(1, true)}` }) : "";
    sec.innerHTML = `
      <div class="ohead"><b class="disp">Daily Orders</b><small>${icon("clock", 12)} ${fmtBadgeEta(SECONDS_PER_DAY - (now % SECONDS_PER_DAY))}${rerolls > 0 ? ` · ${icon("rotate", 12)} ${rerolls}` : ""}</small></div>
      ${checkin}
      <div class="olist">${rows || `<p class="none">${icon("star", 14)} All done today</p>`}</div>
      <div class="ochests">
        ${meter("supply", SUPPLY_ICON, "Supply chest", orders.supplyProgress(now), d.ordersPerDay, orders.suppliesReady)}
        ${meter("weekly", WEEKLY_ICON, "Weekly chest", orders.weeklyProgress(now), d.weeklyGoal, orders.weeklyReady, weeklyExtra)}
      </div>`;
    sec.querySelector<HTMLButtonElement>(".ocheck")?.addEventListener("click", () => ctx.onCheckin());
    for (const b of sec.querySelectorAll<HTMLButtonElement>(".ochart")) b.onclick = () => {
      const weekly = b.dataset.meter === "weekly", B = catalog.balance;
      const extras = weekly ? [
        ...(d.weeklyEssence > 0 ? [{ art: (px: number) => essenceIconHtml(px), name: "Essence", count: fmtCompact(d.weeklyEssence), rarity: null }] : []),
        ...(d.weeklyGems > 0 ? [{ art: (px: number) => gemGlyph(px), name: "Gems", count: String(d.weeklyGems), rarity: null }] : []),
      ] : [];
      showBandFocus(ctx.parent, B, catalog.highestLevelCleared, weekly ? d.weeklyBand : d.supplyBand, weekly ? "Weekly chest" : "Supply chest",
        `<img decoding="sync" src="${chestIconUrl(weekly ? WEEKLY_ICON : SUPPLY_ICON)}" alt="">`, extras);
    };
    for (const b of sec.querySelectorAll<HTMLButtonElement>(".claim")) b.onclick = () => claim(b, Number(b.dataset.uid));
    for (const b of sec.querySelectorAll<HTMLButtonElement>(".reroll")) b.onclick = () => {
      const r = orders.reroll(Number(b.dataset.uid), ctx.now(), catalog.highestLevelCleared);
      if (typeof r === "string") toast(ctx.parent, r === "nothing else to offer" ? "No other order to swap in right now" : "One free swap a day");
      ctx.onChange(); render();
    };
    sec.querySelector<HTMLButtonElement>(".open.supply")?.addEventListener("click", (e) => open(e.currentTarget as HTMLElement, "Supply chest", orders.openSupply(catalog.highestLevelCleared)));
    sec.querySelector<HTMLButtonElement>(".open.weekly")?.addEventListener("click", (e) => open(e.currentTarget as HTMLElement, "Weekly chest", orders.openWeekly(catalog.highestLevelCleared), d.weeklyEssence));
  };
  const claim = (btn: HTMLElement, uid: number): void => {
    const r: OrderReward | null = orders.claim(uid, ctx.now());
    if (!r) return;
    grantReward(catalog.profile, r);
    ctx.onChange();
    flyReward(btn, r.gold);
    if (r.speedUpMinutes > 0 || r.supply) toast(ctx.parent, [r.speedUpMinutes > 0 ? `+${fmtSpeedUp(r.speedUpMinutes)} speed-up` : "", r.supply ? `+1 ${supplyName(catalog.balance, r.supply)}` : ""].filter(Boolean).join(" · "), 1800);
    render();
  };
  const open = (btn: HTMLElement, name: string, chest: ChestResult | null, essence = 0): void => {
    if (!chest) return;
    const gear = catalog.bankChest(chest);
    ctx.onChange();
    render();
    // M48 (Kevin: "it should roll a random chest"): the chest the meter rolled opens like every other chest — the
    // 3D chest in its rolled rarity, TAP to rattle, the burst and the cards — not a toast. The weekly chest's gems
    // (paid by openWeekly) are named once it is collected.
    const gems = name === "Weekly chest" ? catalog.balance.orders.weeklyGems : 0;
    btn.blur();
    rewardChest(ctx.parent, catalog.balance, chest.rarity, { gear, speedUpMinutes: chest.speedUpMinutes, gold: chest.gold, essence }, name,
      () => { if (gems > 0) toast(ctx.parent, `+${gems} gems from the ${name}`, 1800); });
  };
  render();
  return sec;
}
